import { useEffect, useRef } from 'react';
import anime from 'animejs';
import type { CVConfig } from '../../config/cvConfig';

interface SkillsProps {
  skills: CVConfig['skills'];
}

export function Skills({ skills }: SkillsProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animate skill bars
            anime({
              targets: '.skill-bar-fill',
              width: (el) => `${el.getAttribute('data-level')}%`,
              duration: 1500,
              delay: (_el, i) => i * 100,
              easing: 'easeOutQuart'
            });

            // Animate skill cards
            anime({
              targets: '.skill-card',
              translateY: [50, 0],
              opacity: [0, 1],
              duration: 800,
              delay: (_el, i) => i * 200,
              easing: 'easeOutQuart'
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="skills" 
      className="py-20 px-4 sm:px-6 lg:px-8 relative"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-orbitron font-bold neon-text mb-4">
            Skills & Expertise
          </h2>
          <p className="text-xl text-white/70 font-rajdhani max-w-3xl mx-auto">
            Cutting-edge technologies and methodologies that power the future of AI-driven content creation
          </p>
        </div>

        {/* Skills Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {skills.map((category, categoryIndex) => (
            <div
              key={category.category}
              className="skill-card bg-black/40 backdrop-blur-sm rounded-2xl p-6 neon-border hover:bg-black/60 transition-all duration-300"
            >
              {/* Category Header */}
              <div className="mb-6">
                <h3 className="text-2xl font-orbitron font-bold text-[var(--synthwave-neon)] mb-2">
                  {category.category}
                </h3>
                <div className="w-12 h-1 bg-gradient-to-r from-[var(--synthwave-primary)] to-[var(--synthwave-secondary)] rounded-full"></div>
              </div>

              {/* Skills List */}
              <div className="space-y-4">
                {category.items.map((skill, skillIndex) => (
                  <div key={skill.name} className="skill-item">
                    {/* Skill Name and Level */}
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-rajdhani font-medium text-white">
                        {skill.name}
                      </span>
                      <span className="text-sm font-rajdhani text-[var(--synthwave-neon)]">
                        {skill.level}%
                      </span>
                    </div>

                    {/* Skill Bar */}
                    <div className="relative">
                      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="skill-bar-fill h-full bg-gradient-to-r from-[var(--synthwave-primary)] via-[var(--synthwave-secondary)] to-[var(--synthwave-accent)] rounded-full transition-all duration-300"
                          data-level={skill.level}
                          style={{ width: '0%' }}
                        />
                      </div>
                      
                      {/* Glow effect */}
                      <div
                        className="absolute top-0 h-2 bg-gradient-to-r from-[var(--synthwave-primary)] via-[var(--synthwave-secondary)] to-[var(--synthwave-accent)] rounded-full opacity-50 blur-sm transition-all duration-300"
                        style={{ width: '0%' }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Category Stats */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex justify-between text-sm font-rajdhani text-white/60">
                  <span>Skills: {category.items.length}</span>
                  <span>
                    Avg: {Math.round(category.items.reduce((acc, skill) => acc + skill.level, 0) / category.items.length)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Skills Visualization */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-8 p-6 bg-black/40 backdrop-blur-sm rounded-2xl neon-border">
            <div className="text-center">
              <div className="text-3xl font-orbitron font-bold text-[var(--synthwave-neon)]">
                {skills.reduce((acc, category) => acc + category.items.length, 0)}
              </div>
              <div className="text-sm font-rajdhani text-white/70">Total Skills</div>
            </div>
            
            <div className="w-px h-12 bg-white/20"></div>
            
            <div className="text-center">
              <div className="text-3xl font-orbitron font-bold text-[var(--synthwave-primary)]">
                {skills.length}
              </div>
              <div className="text-sm font-rajdhani text-white/70">Categories</div>
            </div>
            
            <div className="w-px h-12 bg-white/20"></div>
            
            <div className="text-center">
              <div className="text-3xl font-orbitron font-bold text-[var(--synthwave-secondary)]">
                {Math.round(
                  skills.reduce((acc, category) => 
                    acc + category.items.reduce((skillAcc, skill) => skillAcc + skill.level, 0), 0
                  ) / skills.reduce((acc, category) => acc + category.items.length, 0)
                )}%
              </div>
              <div className="text-sm font-rajdhani text-white/70">Avg Proficiency</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}